import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { BedrijfSchema } from '@/api';
import {
  usePatchBedrijfId,
  usePostBedrijf,
  usePostLineageBedrijf,
} from '@/api/queries/bedrijf';
import { useBesluit } from '@/api/queries/besluit';
import { Input } from '@/components/Form/Input/Input';
import { Select } from '@/components/Form/Select';
import { validate } from '@/components/Form/utilities';
import { Modal, useModalState } from '@/components/Modal';
import { bevoegdGezag } from '@/constants/bevoegdGezag';
import { PLACEHOLDER_UUID } from '@/constants/placeholder-uuid';
import { createID } from '@/utils/createID';
import { ErrorMessage } from '@/utils/errorMessage';

export type BedrijfsInformatieProps = {
  title: string;
  bedrijfToUpdate?: BedrijfSchema;
};

type FormValues = Pick<
  BedrijfSchema,
  | 'Naam'
  | 'Locatienummer'
  | 'Straatnaam'
  | 'Huisnummer'
  | 'Postcode'
  | 'Plaatsnaam'
  | 'KVKNummer'
  | 'Bevoegd_gezag'
  | 'Vorig'
>;

const FORM_ID = 'popup-bedrijfsinformatie-bewerken-form';

export const BedrijfsInformatie = ({
  title,
  bedrijfToUpdate,
}: BedrijfsInformatieProps) => {
  const navigate = useNavigate();
  const [forceClose, setForceClose] = useState(false);

  const modalState = useModalState();

  const queryClient = useQueryClient();

  const postLineageBedrijf = usePostLineageBedrijf();
  const postBedrijf = usePostBedrijf();
  const patchBedrijfId = usePatchBedrijfId();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    defaultValues: bedrijfToUpdate ?? {},
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<FormValues> = async ({
    Naam,
    Locatienummer,
    Postcode,
    Straatnaam,
    Huisnummer,
    Bevoegd_gezag,
    KVKNummer,
    Plaatsnaam,
  }) => {
    modalState.trigger.action();

    const updatedBedrijf = {
      Naam,
      Postcode,
      Straatnaam,
      Huisnummer,
      Plaatsnaam,
      Bevoegd_gezag,
      // Set default to 0 till backend removes required field Goedgekeurd.
      Goedgekeurd: 0,
      Locatienummer: Locatienummer ?? '',
      KVKNummer: KVKNummer ?? '',
    };

    // If there's a bedrijf to update, update the bedrijf.
    if (bedrijfToUpdate?.ID) {
      const vigerendBesluit = await queryClient.fetchQuery(
        useBesluit.getOptions({
          page: 1,
          size: 1,
          filter: [
            {
              model: 'Besluit',
              field: 'Bedrijf',
              op: 'eq',
              value: bedrijfToUpdate.ID,
            },
            {
              model: 'Besluit',
              field: 'Status',
              op: 'eq',
              value: 'Publicatie',
            },
          ],
        })
      );

      if (vigerendBesluit.objects?.length) {
        postBedrijf.mutate(
          {
            data: {
              ID: createID(),
              ...updatedBedrijf,
              Lineage: bedrijfToUpdate.Lineage,
              Vorig: bedrijfToUpdate.ID,
            },
          },
          {
            onSuccess: () => modalState.trigger.success(),
            onError: (error) => {
              modalState.trigger.error({
                title: 'Mislukt',
                description:
                  ErrorMessage.getLocalErrorMessage(error) ??
                  'Er is iets mis gegaan',
              });
            },
          }
        );
      } else {
        patchBedrijfId.mutate(
          {
            id: bedrijfToUpdate.ID,
            data: {
              ...updatedBedrijf,
              Lineage: bedrijfToUpdate.Lineage,
            },
          },
          {
            onSuccess: () => modalState.trigger.success(),
            onError: (error) => {
              modalState.trigger.error({
                title: 'Mislukt',
                description:
                  ErrorMessage.getLocalErrorMessage(error) ??
                  'Er is iets mis gegaan',
              });
            },
          }
        );
      }
    }

    // If there's no bedrijf to update, create a new bedrijf.
    if (!bedrijfToUpdate) {
      try {
        const lineage = await postLineageBedrijf.mutateAsync({
          data: {
            ID: createID(),
            // Tenant will be replaced by BE
            Tenant: PLACEHOLDER_UUID,
          },
        });

        const bedrijf = await postBedrijf.mutateAsync({
          data: {
            ID: createID(),
            Lineage: lineage.ID,
            ...updatedBedrijf,
          },
        });

        modalState.trigger.success();

        navigate({
          to: '/$bedrijfLineageId/bedrijf/overzicht',
          params: {
            bedrijfLineageId: bedrijf.Lineage,
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error && ErrorMessage.getLocalErrorMessage(error);

        if (errorMessage) {
          modalState.trigger.error({
            title: 'Mislukt',
            description: errorMessage,
          });
        }
      }
    }
  };

  const warnBeforeClose =
    Object.keys(dirtyFields).length > 0 &&
    modalState.modalProps.action?.type !== 'success';

  return (
    <Modal
      size="medium"
      title={title}
      isClosingDisabled={
        !!(modalState.modalProps.isClosingDisabled || warnBeforeClose)
      }
      forceClose={forceClose || modalState.modalProps.forceClose}
      beforeClose={() => {
        if (warnBeforeClose) {
          if (
            window.confirm(
              'Weet je zeker dat je dit venster wilt sluiten? Alle ingevulde gegevens gaan verloren.'
            )
          ) {
            setForceClose(true);
          }
        }
      }}
      description={<p className="sr-only">Vul hier de bedrijfsinformatie in</p>}
      modalStateProps={modalState.modalProps}
    >
      <form
        id={FORM_ID}
        className="flex w-full flex-col gap-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          {...register('Naam', { required: 'Vul een bedrijfsnaam in' })}
          id="Naam"
          placeholder="Bedrijfsnaam"
          error={errors.Naam}
          label="Bedrijfsnaam"
          maxLength={100}
          required
        />

        <Input
          {...register('Locatienummer')}
          placeholder="Locatienummer"
          id="locatie_nummer"
          label="Locatienummer"
          maxLength={100}
          error={errors.Locatienummer}
        />

        <Input
          {...register('Straatnaam', { required: 'Vul een straatnaam in' })}
          placeholder="Straatnaam"
          id="street_address"
          label="Straatnaam"
          error={errors.Straatnaam}
          maxLength={100}
          required
        />

        <Input
          {...register('Huisnummer', {
            required: 'Vul een huisnummer in',
          })}
          placeholder="Nummer"
          id="huisnummer"
          error={errors.Huisnummer}
          maxLength={20}
          label="Huisnummer"
          required
        />

        <Input
          {...register('Postcode', {
            required: 'Vul een postcode in',
            validate: validate.zipcode(),
            onChange: (e) => {
              const cleanedValue = e.target.value
                // Remove spaces
                .replace(/\s/g, '')
                // Capitalize
                .toUpperCase();

              setValue('Postcode', cleanedValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
          })}
          id="zip"
          label="Postcode"
          maxLength={20}
          placeholder="Postcode"
          error={errors.Postcode}
          required
        />

        <Input
          {...register('Plaatsnaam', { required: 'Vul een plaatsnaam in' })}
          placeholder="Plaatsnaam"
          id="city"
          label="Plaatsnaam"
          maxLength={100}
          error={errors.Plaatsnaam}
          required
        />

        <Input
          {...register('KVKNummer', {
            validate: validate.KVK(),
          })}
          label="KvK nummer"
          maxLength={8}
          placeholder="KvK nummer"
          id="kvk_nummer"
          error={errors.KVKNummer}
        />

        <Select
          {...register('Bevoegd_gezag', {
            required: 'Vul het bevoegde gezag in',
          })}
          id="Bevoegd_gezag"
          label="Bevoegd gezag"
          defaultValue=""
          error={errors.Bevoegd_gezag}
          required
        >
          <option value={''} disabled>
            Selecteer een optie
          </option>
          {bevoegdGezag.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>

        <Modal.Footer>
          <Modal.Close />

          <Modal.Action type="submit" successLabel="Opgeslagen">
            Opslaan
          </Modal.Action>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
